import { getWindowObject } from '@eventbrite/feature-detection';
import { StatsigUser } from '@eventbrite/statsig';
import { EventStatus } from '../constants/eventStatus';
import { StatsigConf } from '../models/statsig';
import { Ticket } from '../models/tickets';
import { CheckoutState } from '../state';
import {
    getScreenWidth,
    getUserAgent,
    isEventbriteTLDInDevOrQAEnv,
} from '../utils/browser';
import {
    hasRemainingQuantityDisplayEnabled,
    hasSomeTicketWithMoreThanOneVariant,
} from '../utils/tickets';
import {
    getCorrelationId,
    getIsRegEvent,
    getSessionId,
    hasFewTicketsLeftUrgencySignal,
    hasSalesEndSoonUrgencySignal,
    isAndroidApp,
    isEventbriteTLD,
    isIosApp,
} from './app';
import {
    getEventId,
    getEventStatus,
    getIsEventOnline,
    getIsFreeEvent,
    getIsReservedSeating,
    getOrganizerId,
    getTicketReseller,
    getWaitlistSettings,
    isDonation,
    isListedEvent,
    isLockedEvent,
    isSeries,
    isSeriesParent,
} from './event';
import {
    hasQuestions,
    isPaypalAvailableAsPaymentMethod,
} from './expressCheckout';
import { isLoginInterstitialSplitTreatmentEnabled } from './splitio';
import { getHasDonationTicketsAvailable, getTicketsById } from './tickets';
import { isAuthenticated, isUserLoggedIn } from './user';

export const isStatsigFreeTrialEnabledForInterstitialLogin = (
    state: CheckoutState,
): boolean =>
    (isEventbriteTLD(state) || isEventbriteTLDInDevOrQAEnv()) &&
    isLoginInterstitialSplitTreatmentEnabled(state);

export const isStatsigFreeTrialEnabled = (state: CheckoutState) =>
    isStatsigFreeTrialEnabledForInterstitialLogin(state);

//TODO To remove after Redux store initialization refactor - EB-214132
export const getInitialStatsigCustomUserConfig = (
    props: StatsigConf,
): StatsigUser => {
    const { userAgent } = getWindowObject('navigator') ?? {};

    return {
        custom: {
            eventId: getEventId(props),
            isEventbriteTLD: props.isEventbriteTLD,
            ...(userAgent
                ? { userAgentLowerCase: userAgent.toLowerCase() }
                : {}),
            hasMoreThanOneTicketType: props.tickets?.length > 1,
            hasMultivariantTickets: hasSomeTicketWithMoreThanOneVariant(
                props.tickets,
            ),
            hasTicketResellers: !!props.event?.ticket_reseller,
            hasDonationTicketsAvailable:
                props.event?.properties?.has_donation_tickets_available,
            hasFewTicketsLeftUrgencySignal:
                props.urgencySignals?.messages?.fewTickets ?? false,
            hasSalesEndSoonUrgencySignal:
                props.urgencySignals?.messages?.salesEndSoon ?? false,
            hasScreenWidth: getScreenWidth(),
            hasRemainingQuantityDisplay: hasRemainingQuantityDisplayEnabled(
                props.tickets,
            ),
            isDonation: props.event?.properties?.is_donation,
            isFree: props.event?.is_free,
            isLive: getEventStatus(props) === EventStatus.live,
            isLocked: props.event?.is_locked,
            isPaid: props.event?.properties?.is_paid,
            // TODO To change after Redux initialization refactor - EB-214132
            // Initially isPaypalEPPAvailable will always be false so this harcoded value cause no issue
            // but after the refactor it should use isPaypalAvailableAsPaymentMethod(state)
            isPaypalEPPAvailable: false,
            isRegistrationEvent: props.isRegistrationEvent,
            isReservedSeating: props.event?.is_reserved_seating,
            isSeries: props.event?.is_series,
            isSeriesParent: props.event?.is_series_parent,
            isUserLoggedIn: props.user?.isAuthenticated,
            isWaitlist: !!props.event?.waitlist_settings,
            listed: isListedEvent(props),
            onlineEvent: props.event?.online_event,
            useAndroidProfile: props.useAndroidProfile,
            useIosProfile: props.useIosProfile,
        },
        customIDs: {
            correlationId: props.request?.correlation_id,
            eventId: getEventId(props),
            organizerId: getOrganizerId(props),
            sessionId: props.request?.session_id,
        },
    };
};

export const getStatsigConfigFromState = (state: CheckoutState) => {
    const userAgent = getUserAgent() ?? {};
    const tickets: Ticket[] = Object.values(getTicketsById(state));
    const isFree = !!getIsFreeEvent(state);
    const isLoggedIn = isAuthenticated(state) || isUserLoggedIn(state);

    return {
        custom: {
            eventId: getEventId(state),
            isEventbriteTLD: isEventbriteTLD(state),
            ...(userAgent
                ? { userAgentLowerCase: userAgent.toLowerCase() }
                : {}),
            hasMoreThanOneTicketType: tickets.length > 1,
            hasMultivariantTickets:
                hasSomeTicketWithMoreThanOneVariant(tickets),
            hasTicketResellers: !!getTicketReseller(state),
            hasDonationTicketsAvailable: getHasDonationTicketsAvailable(state),
            hasFewTicketsLeftUrgencySignal:
                hasFewTicketsLeftUrgencySignal(state),
            hasQuestions: hasQuestions(state),
            hasSalesEndSoonUrgencySignal: hasSalesEndSoonUrgencySignal(state),
            hasScreenWidth: getScreenWidth(),
            hasRemainingQuantityDisplay: hasRemainingQuantityDisplayEnabled(
                state.app?.tickets as Ticket[],
            ),
            isDonation: isDonation(state),
            isFree: isFree,
            isLive: getEventStatus(state) === EventStatus.live,
            isLocked: isLockedEvent(state),
            isPaid: !isFree,
            isPaypalEPPAvailable: isPaypalAvailableAsPaymentMethod(state),
            isRegistrationEvent: !!getIsRegEvent(state),
            isReservedSeating: getIsReservedSeating(state),
            isSeries: isSeries(state),
            isSeriesParent: isSeriesParent(state),
            isUserLoggedIn: isLoggedIn,
            isWaitlist: !!getWaitlistSettings(state),
            listed: isListedEvent(state),
            onlineEvent: getIsEventOnline(state),
            useAndroidProfile: isAndroidApp(state),
            useIosProfile: isIosApp(state),
        },
        customIDs: {
            correlationId: getCorrelationId(state),
            eventId: getEventId(state),
            organizerId: getOrganizerId(state),
            sessionId: getSessionId(state),
        },
    };
};
